<!-- App.vue -->
<template>
  <div class="app" :class="{ 'mobile': portfolioStore.isMobile }">
    <div v-if="portfolioStore.isLoading" class="loading">
      Loading...
    </div>
    <template v-else>
      <router-view v-slot="{ Component }">
        <transition :name="transitionName" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { usePortfolioStore } from '@/stores/portfolio'
import { useTextScale, useSemanticColors } from '@/composables/useStyleVariables'
import { useRoute, useRouter } from 'vue-router'

// Route handling
const portfolioStore = usePortfolioStore();
const router = useRouter();
const route = useRoute();

// Style
const { fontSize, lineHeight } = useTextScale();
const { bgColors, textColors, buttonColors } = useSemanticColors();

const transitionName = ref('fade');

// Function to attempt scroll restoration multiple times
const attemptScrollRestore = async (savedPosition, attempts = 5, delay = 100) => {
  if (attempts <= 0) {
    return;
  }

  window.scrollTo({
    left: savedPosition.x,
    top: savedPosition.y,
    behavior: 'smooth'
  });

  // Check if scroll position was set correctly
  if (Math.abs(window.scrollY - savedPosition.y) > 1) {
    await new Promise(resolve => setTimeout(resolve, delay));
    await attemptScrollRestore(savedPosition, attempts - 1, delay);
  }
};

// Save scroll position before route changes
router.beforeEach((to, from, next) => {
  // Save the scroll position for the current route before navigating
  portfolioStore.setRouteScrollPosition({
    path: from.path,
    global: {
      x: window.scrollX,
      y: window.scrollY
    }
  });
  next();
});

// Handle scroll position restoration after route changes
watch(
  () => route.path,
  async (newPath) => {
    // Wait for initial DOM update
    await nextTick();
    
    // Add a small delay to ensure components have mounted
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Check if we have a saved position for this route
    const savedPosition = portfolioStore.routeScrollPosition[newPath]?.global;

    if (savedPosition) {
      await attemptScrollRestore(savedPosition);
    } else {
      window.scrollTo(0, 0);
    }
  }
);

// Lifecycle hooks
const initializeApp = async (retries = 3) => {
  try {
    portfolioStore.isLoading = true; // Set loading at start of initialization

    // Wait for portfolio data
    if (!portfolioStore.isLoaded) {
      await portfolioStore.fetchPortfolioData()
    }

    // Initial viewport setup
    portfolioStore.updateViewport()
    portfolioStore.isLoading = false; // Set loading to false after everything is ready


  } catch (error) {
    if (retries > 0) {
      console.error(`Error initializing app, retrying... (${retries} attempts left)`)
      await new Promise(resolve => setTimeout(resolve, 1000))
      return initializeApp(retries - 1)
    } else {
      console.error('Failed to initialize app after multiple attempts:', error)
      portfolioStore.isLoading = false; // Make sure to clear loading state even on error
      router.push('/404')
    }
  }
}

let touchMoveBlocker = null

watch(
  () => portfolioStore.overflowHidden,
  (hide) => {
    const overflowValue = hide ? 'hidden' : 'auto'
    document.documentElement.style.overflowY = overflowValue
    document.body.style.overflowY = overflowValue

    if (touchMoveBlocker) {
      document.removeEventListener('touchmove', touchMoveBlocker)
      touchMoveBlocker = null
    }

    if (hide) {
      touchMoveBlocker = (e) => e.preventDefault()
      document.addEventListener('touchmove', touchMoveBlocker, { passive: false })
    }
  }
)

const handleResize = () => portfolioStore.updateViewport();
const handleScroll = () => portfolioStore.handleScroll();

onMounted(async () => {
  await initializeApp();
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll);
  portfolioStore.handleScroll();
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style>
/* Non-scoped (global) styles here */
@import url("https://use.typekit.net/mia7dtk.css");

* {
  font-family: "futura-pt", sans-serif;
  font-weight: 300;
  font-style: normal;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  font-size: v-bind('fontSize.body + "px"');
  height: auto;
  min-height: 100%;
  overflow-y: auto;
}

ul {
  list-style-type: none;
  color: inherit;
}

li {
  text-decoration: none;
  color: inherit;
}

a {
  font-size: v-bind('fontSize.body + "px"');
  font-family: "franklin-gothic-condensed", 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
  display: inline;
  text-decoration: none;
  text-align: left;
  color: v-bind('textColors.accent');
  cursor: pointer;
  pointer-events: auto;
}

a:hover {
  color: v-bind('buttonColors.hover');
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: "futura-pt", sans-serif;
  font-weight: 300;
  font-style: normal;
}

p,
li {
  font-size: v-bind('fontSize.body + "px"');
  font-family: "franklin-gothic-condensed", 'Franklin Gothic Medium', 'Arial Narrow', Arial, sans-serif;
}

h1 {
  font-size: v-bind('fontSize.h1 + "px"');
  line-height: v-bind('lineHeight.h1 + "px"');
}

h2 {
  font-size: v-bind('fontSize.h2 + "px"');
  line-height: v-bind('lineHeight.h2 + "px"');
}

h3 {
  font-size: v-bind('fontSize.h3 + "px"');
  line-height: v-bind('lineHeight.h3 + "px"');
}

h4 {
  font-size: v-bind('fontSize.h4 + "px"');
  line-height: v-bind('lineHeight.h4 + "px"');
}

h5 {
  font-size: v-bind('fontSize.h5 + "px"');
  line-height: v-bind('lineHeight.h5 + "px"');
}

h6 {
  font-size: v-bind('fontSize.h6 + "px"');
  line-height: v-bind('lineHeight.h6 + "px"');
}

.mobile-spacer {
  width: 100%;
  height: v-bind('portfolioStore.mobileSpacerHeight + "px"');
}

.loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind('bgColors.light');
  color: v-bind('textColors.bgDark');
  font-size: v-bind('fontSize.h5 + "px"');
}
</style>